import React, { FC, memo, useCallback, useState } from 'react';
import { Trans } from 'react-i18next';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { getLoggedUserData, useGetUserByIdQuery, useRole, UserRoles } from 'entities/User';
import { Link, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { useGetCurrencySymbol } from 'app/appState';
import { AppRoutes } from 'app/config/routerConfig/types';
import { LatestActivity } from './LatestActivity';
import { PersonalInvoices } from './PersonalAccounts';
import { UserBalanceHistory } from './UserBalanceHistory';
import { PersonalContracts } from './PersonalContracts';
import { ContractDetailsDrawer } from 'widgets/ContractDetailsDrawer';
import { ReactComponent as AskUsIcon } from 'shared/assets/icons/newIcons/AskUsIcon.svg';
import { requestFormModalActions, RequestModalMode } from 'widgets/RequestModal';
import { Button } from 'shared/ui/Button';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { ReactComponent as AccountIcon } from 'shared/assets/icons/newIcons/AccountIcon.svg';
import { ReactComponent as CallIcon } from 'shared/assets/icons/newIcons/CallIcon.svg';
import { ReactComponent as ClockIcon } from 'shared/assets/icons/newIcons/TimeIcon.svg';
import { ReactComponent as SizeIcon } from 'shared/assets/icons/newIcons/SizeIcon.svg';
import { ReactComponent as DocumentIcon } from 'shared/assets/icons/newIcons/DocumentIcon.svg';
import { ReactComponent as PassportIcon } from 'shared/assets/icons/newIcons/PassportIcon.svg';
import { ReactComponent as BookingIcon } from 'shared/assets/icons/newIcons/BookingIcon.svg';
import { ReactComponent as RightArrowIcon } from 'shared/assets/icons/newIcons/RightArrow.svg';
import { LogOutButton } from 'features/HeaderAuthAction';
import { AccountDetailsForm } from 'widgets/AccountDetailsForm';
import { CustomerDetailsForm } from 'widgets/CustomerDetailsForm';
import { AccessDetailsForm } from './AccessDetailsForm';
import { Invoice, PaymentModal } from 'entities/Invoice';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

export const ProfilePage: FC = memo(() => {
  const { t } = useAppTranslation(['profile', 'booking', 'common', 'contracts', 'payment']);

  const [selectedUnpaidInvoice, setSelectedUnpaidInvoice] = useState<Nullable<Invoice>>(null);

  const { userId } = useParams();
  const { data } = useGetUserByIdQuery({ userId, params: { warehouses: true, userDebt: true } });

  const dispatch = useAppDispatch();

  const loggedUser = useAppSelector(getLoggedUserData);

  const { isDesktop } = useScreenBreakpoints();

  const { isManagement } = useRole();

  const loggedUserEmail = loggedUser?.email;

  const currencySymbol = useGetCurrencySymbol();

  const openPaymentModal = useCallback((invoice: Invoice): void => {
    setSelectedUnpaidInvoice(invoice);
  }, []);

  const closePaymentModal = useCallback((): void => {
    setSelectedUnpaidInvoice(null);
  }, []);

  if (!userId) {
    return <div>{t('User not exists')}</div>;
  }

  if (!data) {
    return <div>{t('User not authorized')}</div>;
  }

  const openRequestModal = (): void => {
    dispatch(
      requestFormModalActions.openRequestModal({
        title: t('Let`s get to know each other better!', { ns: 'booking' }),
        description: t('Please provide us with your information so that we can suggest the best pricing options that meet your needs.', {
          ns: 'booking',
        }),
        mode: RequestModalMode.CONTACT_US,
      }),
    );
  };

  const {
    firstName,
    lastName,
    email,
    phone,
    dateOfBirth,
    country,
    city,
    street,
    zip,
    house,
    apartment,
    balance,
    language,
    identificationNumber,
    additionalEmail,
    company,
    accountType,
    bankingRequisites,
    role,
    permissions,
    warehouses,
    userDebt,
    isInDebtCollection,
    debtCollectionNote,
    additionalPhone,
    profileVerificationStatus,
    identificationDocIssuingAuthority,
    identificationDocIssuingDate,
    residenceRegistration,
    signingIdentificator,
    middleName,
  } = data;

  const mobileView = (): JSX.Element => {
    return (
      <div className="px-4">
        <div className="flex flex-col p-6 bg-white rounded-lg gap-2 min-h-[650px]">
          <div className="flex flex-col">
            <div>
              <Trans
                t={t}
                i18nKey="<1>{{firstName}} {{lastName}}</1>"
                components={[
                  <span key="0" className="text-3xl font-semibold font-tektur" />,
                  <Link
                    key="1"
                    className="text-3xl font-semibold text-accent font-tektur"
                    to={isManagement ? `${AppRoutes.USERS}/${userId}/contracts` : ''}
                  />,
                ]}
                values={{ firstName: firstName || company, lastName }}
              />
            </div>

            <div className="flex gap-3 text-primary items-center text-xl font-tektur">
              {t('Balance: {{balance}}', { balance: formatPrice(balance) ?? 0 })} {currencySymbol}
              {/* <UserBalanceHistory userId={userId} balance={balance ?? 0} /> */}
            </div>

            <div className="mt-8 flex flex-col gap-8">
              <Link to={`${AppRoutes.PROFILE}/${userId}/account`} className="flex justify-between items-center text-primary w-full">
                <div className="flex gap-3 items-center text-lg font-tektur">
                  <AccountIcon width={25} height={25} />
                  {t('My account', { ns: 'common' })}
                </div>
                <RightArrowIcon />
              </Link>

              <Link to={AppRoutes.MY_BOXES} className="flex justify-between items-center text-primary w-full">
                <div className="flex gap-3 items-center text-lg font-tektur">
                  <SizeIcon width={23} height={23} />
                  {t('My bookings', { ns: 'common' })}
                </div>
                <RightArrowIcon />
              </Link>

              <Link to={`${AppRoutes.PROFILE}/${userId}/latest-activity`} className="flex justify-between items-center text-primary w-full">
                <div className="flex gap-3 items-center text-lg font-tektur">
                  <ClockIcon width={25} height={25} />
                  {t('Latest Activity')}
                </div>
                <RightArrowIcon />
              </Link>

              <Link to={`${AppRoutes.PROFILE}/${userId}/invoices`} className="flex justify-between items-center text-primary w-full">
                <div className="flex gap-3 items-center text-lg font-tektur">
                  <DocumentIcon width={26} height={26} />
                  {t('Personal invoices')}
                </div>
                <RightArrowIcon />
              </Link>

              <div className="flex justify-between items-center text-primary w-full cursor-pointer" onClick={openRequestModal}>
                <div className="flex gap-3 items-center text-lg font-tektur">
                  <CallIcon width={25} height={25} />
                  {t('Contact us', { ns: 'common' })}
                </div>
                <RightArrowIcon />
              </div>

              <Link to={AppRoutes.BOOKING} className="flex justify-between items-center text-primary text-lg w-full">
                <div className="flex gap-3 items-center text-lg font-tektur">
                  <BookingIcon width={25} height={25} />
                  {t('Booking', { ns: 'common' })}
                </div>
                <RightArrowIcon />
              </Link>

              {isManagement && (
                <Link to={AppRoutes.ADMIN_PANEL} className="flex justify-between items-center text-primary text-lg w-full">
                  <div className="flex gap-3 items-center text-lg font-tektur">
                    <PassportIcon width={25} height={25} />
                    {t('Admin panel', { ns: 'common' })}
                  </div>
                  <RightArrowIcon />
                </Link>
              )}
            </div>
          </div>
        </div>
        <div className="fixed bottom-0 left-0 w-full z-20 bg-backgroundColor">
          <div className="flex justify-center p-2 px-4">
            <LogOutButton className="w-full" theme="dark" />
          </div>
        </div>
      </div>
    );
  };

  const desktopView = (): JSX.Element => {
    return (
      <div className="pt-7 flex justify-between max-w-[1200px] mx-auto">
        <div className="basis-[65%] space-y-6">
          <div className="flex flex-col p-5 bg-white rounded-lg gap-2">
            <div className="flex justify-between">
              <div>
                <Trans
                  t={t}
                  i18nKey="<0>Profile</0> <1>{{firstName}} {{lastName}}</1>"
                  components={[
                    <span key="0" className="text-3xl font-semibold font-tektur pr-1" />,
                    <Link
                      key="1"
                      className="text-2xl font-semibold text-accent font-tektur"
                      to={isManagement ? `${AppRoutes.USERS}/${userId}/contracts` : ''}
                    />,
                  ]}
                  values={{ firstName: firstName || company, lastName }}
                />
              </div>

              <div className="flex gap-3 text-primary items-center text-lg font-tektur">
                {t('Balance: {{balance}}', { balance: formatPrice(balance) ?? 0 })} {currencySymbol}
                <UserBalanceHistory userId={userId} balance={balance ?? 0} />
              </div>
            </div>
            <PersonalContracts userId={userId} openPaymentModal={openPaymentModal} />
          </div>

          {(role === UserRoles.MANAGER || role === UserRoles.STAFF || role === UserRoles.PARTNER) && (
            <div className="p-5 bg-white rounded-lg">
              <AccessDetailsForm userId={userId} role={role} permissions={permissions} assignedWarehouses={warehouses} />
            </div>
          )}

          <div className="p-5 bg-white rounded-lg">
            <LatestActivity email={email} loggedUserEmail={loggedUserEmail} />
          </div>

          <div className="px-5 pt-5 pb-2 bg-white rounded-lg">
            <PersonalInvoices userId={userId} openPaymentModal={openPaymentModal} />
          </div>
        </div>

        <div className="flex flex-col basis-1/3 space-y-12">
          <div className="flex space-x-25">
            <div className="w-full flex flex-col gap-2">
              <AccountDetailsForm
                userId={userId}
                email={email}
                phone={phone}
                firstName={firstName}
                lastName={lastName}
                accountType={accountType}
                company={company}
                identificationNumber={identificationNumber}
                loggedUserId={loggedUser?.userId}
                profileVerificationStatus={profileVerificationStatus}
                dateOfBirth={dateOfBirth}
                identificationDocIssuingAuthority={identificationDocIssuingAuthority}
                identificationDocIssuingDate={identificationDocIssuingDate}
                residenceRegistration={residenceRegistration}
                signingIdentificator={signingIdentificator}
                middleName={middleName}
              />
              <CustomerDetailsForm
                userId={userId}
                dateOfBirth={dateOfBirth}
                email={email}
                additionalEmail={additionalEmail}
                language={language}
                bankingRequisites={bankingRequisites}
                phone={phone}
                additionalPhone={additionalPhone}
                country={country}
                city={city}
                street={street}
                zip={zip}
                house={house}
                apartment={apartment}
                firstName={firstName}
                lastName={lastName}
                accountType={accountType}
                company={company}
                role={role}
                identificationNumber={identificationNumber}
              />

              <div className="flex flex-col p-4 bg-white rounded-lg gap-2">
                <AskUsIcon className="w-[45px] h-[45px] shrink-0" />
                <div className="text-2xl flex font-semibold font-tektur">{t('Ask us')}</div>

                <div className="text-primaryLight text-lg">
                  {t('If you have any questions or encounter a problem, we are here to help you at any time')}
                </div>

                <Button onClick={openRequestModal}>{t('Ask a question')}</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const content = isDesktop ? desktopView() : mobileView();

  return (
    <>
      {content}
      <ContractDetailsDrawer />
      {selectedUnpaidInvoice && <PaymentModal invoice={selectedUnpaidInvoice} onClose={closePaymentModal} isOpen={true} />}
    </>
  );
});
